import DOMPurify from 'dompurify';
import {isTrustedVideoUrl} from './validateVideoUrl';

/**
 * Validate and sanitize iframe src URLs to only allow trusted video sources
 * @param {string} html - HTML string potentially containing iframes
 * @returns {string} - HTML with validated iframes
 */
function validateIframeSrc(html) {
  if (!html.includes('iframe')) return html;

  const div = document.createElement('div');
  div.innerHTML = html;

  const iframes = div.querySelectorAll('iframe');
  iframes.forEach(iframe => {
    const src = iframe.getAttribute('src') || '';
    if (!isTrustedVideoUrl(src)) iframe.remove();
  });

  return div.innerHTML;
}

/**
 * Sanitize HTML string to prevent XSS attacks
 * @param {string} str - The string to sanitize
 * @returns {string} - The sanitized string
 */
export function cleanString(str) {
  if (typeof str !== 'string') return str;

  // First pass: DOMPurify sanitization
  let sanitized = DOMPurify.sanitize(str, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|xxx|data|blob):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    FORBID_ATTR: ['onerror', 'onload', 'onclick'],
    USE_PROFILES: {html: true},
    ALLOW_DATA_ATTR: false
  });

  // Second pass: Validate iframe sources
  sanitized = validateIframeSrc(sanitized);

  return sanitized;
}

/**
 * Recursively sanitize HTML in object values to prevent XSS attacks
 * @param {any} obj - The object to process
 * @returns {any} - The object with sanitized HTML strings
 */
export function cleanObjectBlockXSSAttack(obj) {
  if (typeof obj === 'string') {
    return cleanString(obj);
  }
  if (Array.isArray(obj)) {
    return obj.map(cleanObjectBlockXSSAttack);
  }
  if (obj && typeof obj === 'object') {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = cleanObjectBlockXSSAttack(value);
    }
    return result;
  }
  return obj;
}
